import { useMemo } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  formatWeekRange,
  isCurrentWeek,
  type WeekKey,
} from "../utils/week-utils";
import { type WeeklyShiftGroup } from "../hooks/useTimeEntryShifts";

interface TimeEntryWeekSelectorProps {
  selectedWeek: WeekKey;
  onWeekChange: (weekKey: WeekKey) => void;
  weeklyGroups: WeeklyShiftGroup[];
}

/**
 * Get the common timezone from a group's shifts, if all shifts share the same timezone.
 */
function getGroupTimezone(group: WeeklyShiftGroup): string | undefined {
  if (group.shifts.length === 0) {
    return undefined;
  }
  const firstTz = group.shifts[0].timezoneId;
  const allSame = group.shifts.every((s) => s.timezoneId === firstTz);
  return allSame ? firstTz : undefined;
}

export function TimeEntryWeekSelector({
  selectedWeek,
  onWeekChange,
  weeklyGroups,
}: TimeEntryWeekSelectorProps) {
  const weekKeys = weeklyGroups.map((g) => g.weekKey);
  const currentIndex = weekKeys.indexOf(selectedWeek);

  // Derive timezone from groups to determine "current week" accurately
  // Use the first group's timezone as a reasonable default
  const defaultTimezone = useMemo(() => {
    if (weeklyGroups.length === 0) {
      return undefined;
    }
    return getGroupTimezone(weeklyGroups[0]);
  }, [weeklyGroups]);

  const canGoPrevious = currentIndex < weekKeys.length - 1;
  const canGoNext = currentIndex > 0;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onWeekChange(weekKeys[currentIndex + 1]);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onWeekChange(weekKeys[currentIndex - 1]);
    }
  };

  const selectedGroup = weeklyGroups.find((g) => g.weekKey === selectedWeek);

  return (
    <div className="flex items-center gap-3 mb-6">
      {/* Navigation buttons */}
      <div className="flex items-center rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        <Button
          variant="ghost"
          size="icon"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          className="h-9 w-9"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleNext}
          disabled={!canGoNext}
          className="h-9 w-9"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Week selector dropdown */}
      <Select value={selectedWeek} onValueChange={onWeekChange}>
        <SelectTrigger className="w-[280px] bg-white">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            <SelectValue placeholder="Select week" />
          </div>
        </SelectTrigger>
        <SelectContent>
          {weeklyGroups.map((group) => (
            <SelectItem key={group.weekKey} value={group.weekKey}>
              <div className="flex items-center justify-between w-full gap-4">
                <span
                  className={cn(
                    isCurrentWeek(
                      group.weekKey,
                      getGroupTimezone(group) ?? defaultTimezone,
                    ) && "font-semibold text-blue-600",
                  )}
                >
                  {formatWeekRange(group.weekKey)}
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {group.totalShifts}
                  </Badge>
                  {group.rejectedCount > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {group.rejectedCount} rejected
                    </Badge>
                  )}
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Quick stats */}
      {selectedGroup && (
        <div className="hidden sm:flex items-center gap-4 text-sm text-slate-500">
          <span>
            <strong className="text-slate-700">
              {selectedGroup.totalShifts}
            </strong>{" "}
            shifts
          </span>
          {selectedGroup.rejectedCount > 0 && (
            <span className="text-red-600">
              <strong>{selectedGroup.rejectedCount}</strong> need resubmission
            </span>
          )}
        </div>
      )}
    </div>
  );
}
